import React, { useState, useEffect } from 'react';
import { Database, Settings, ToggleLeft, ToggleRight, Save, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { knowledgeSourceSettingsService, CRMDataSettings } from '@/services/knowledgeSourceSettings';

interface CRMDataConfigProps {
  onUpdate?: () => void;
}

export const CRMDataConfig: React.FC<CRMDataConfigProps> = ({ onUpdate }) => {
  const { toast } = useToast();
  const [crmEnabled, setCrmEnabled] = useState(false);
  const [dataTypes, setDataTypes] = useState<CRMDataSettings>({
    projects_enabled: true,
    tickets_enabled: true,
    tasks_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load current settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const sourceSettings = await knowledgeSourceSettingsService.getSourceSettings('crm_data');
      
      if (sourceSettings) {
        setCrmEnabled(sourceSettings.is_enabled);
        
        if (sourceSettings.configuration) {
          setDataTypes({
            projects_enabled: sourceSettings.configuration.projects_enabled ?? true,
            tickets_enabled: sourceSettings.configuration.tickets_enabled ?? true,
            tasks_enabled: sourceSettings.configuration.tasks_enabled ?? true
          });
        }
      }
    } catch (error) {
      console.error('Error loading CRM settings:', error);
      toast({
        title: "Error",
        description: "Failed to load CRM settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDataType = (type: keyof CRMDataSettings) => {
    setDataTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    setHasChanges(true);
  };

  const handleCrmToggle = (checked: boolean) => {
    setCrmEnabled(checked);
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      const success = await knowledgeSourceSettingsService.updateCRMSettings(
        crmEnabled,
        dataTypes
      );

      if (success) {
        toast({
          title: "Success",
          description: "CRM settings saved successfully"
        });
        setHasChanges(false);
        onUpdate?.();
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving CRM settings:', error);
      toast({
        title: "Error",
        description: "Failed to save CRM settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            CRM Data Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable CRM Integration */}
          <Card className="border-dashed">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Live CRM Data Integration</h3>
                  <p className="text-sm text-muted-foreground">
                    Allow AI to access real-time project and customer data
                  </p>
                </div>
                <Switch
                  checked={crmEnabled}
                  onCheckedChange={handleCrmToggle}
                />
              </div>
            </CardContent>
          </Card>

          {/* Simplified Data Type Selection - Only 3 toggles */}
          {crmEnabled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Data Types to Include</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">Projects</Label>
                    <p className="text-sm text-muted-foreground">
                      Project information including customer details, appointments, and documents
                    </p>
                  </div>
                  <Switch
                    checked={dataTypes.projects_enabled}
                    onCheckedChange={() => handleToggleDataType('projects_enabled')}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">Support Tickets</Label>
                    <p className="text-sm text-muted-foreground">
                      Support ticket creation and status queries
                    </p>
                  </div>
                  <Switch
                    checked={dataTypes.tickets_enabled}
                    onCheckedChange={() => handleToggleDataType('tickets_enabled')}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">Tasks</Label>
                    <p className="text-sm text-muted-foreground">
                      Task assignments and completion status
                    </p>
                  </div>
                  <Switch
                    checked={dataTypes.tasks_enabled}
                    onCheckedChange={() => handleToggleDataType('tasks_enabled')}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Configuration Options */}
          {crmEnabled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Privacy & Access Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-medium mb-2">Data Freshness</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      How often to refresh CRM data for AI queries
                    </p>
                    <Badge variant="outline">Real-time</Badge>
                  </div>

                  <div className="p-3 border rounded-lg">
                    <h4 className="font-medium mb-2">Access Control</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      User permissions for CRM data access
                    </p>
                    <Badge variant="secondary">Role-based</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          {hasChanges && (
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Information Box */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-blue-900 mb-2">How CRM Data Integration Works</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• AI queries check these settings before accessing any CRM data</li>
                <li>• Projects include customer details, appointments, and documents</li>
                <li>• Support tickets can be created and queried when enabled</li>
                <li>• Tasks show assignments and completion status</li>
                <li>• Data is filtered based on user permissions and tenant isolation</li>
                <li>• Settings are saved to the database and persist across sessions</li>
              </ul>
            </CardContent>
          </Card>

          {/* Example Queries */}
          {crmEnabled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Example AI Responses with CRM Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dataTypes.projects_enabled && (
                  <div className="text-sm font-mono bg-white p-3 rounded border">
                    <strong>Query:</strong> "What's the status of the Johnson project?"<br/>
                    <strong>Response:</strong> "The Johnson residential project is currently in the Installation stage. Site survey was completed on March 15th, and the installation is scheduled for March 28th."
                  </div>
                )}
                {dataTypes.tickets_enabled && (
                  <div className="text-sm font-mono bg-white p-3 rounded border">
                    <strong>Query:</strong> "Create a support ticket for a broken inverter"<br/>
                    <strong>Response:</strong> "I've created ticket #2024-1234 for the inverter issue. A support specialist will contact you within 24 hours."
                  </div>
                )}
                {dataTypes.tasks_enabled && (
                  <div className="text-sm font-mono bg-white p-3 rounded border">
                    <strong>Query:</strong> "What tasks are assigned to me?"<br/>
                    <strong>Response:</strong> "You have 3 open tasks: Site survey for Smith residence (due today), Permit submission for Oak Street (due tomorrow), and Final inspection for Green Valley (due Friday)."
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};